import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import type {
  ApiKey,
  CreateApiKeyData,
  UpdateApiKeyData,
  ApiKeyFilters,
  ApiKeyPagination,
  CreateApiKeyResponse,
  ApiKeyUsageStats,
  ApiKeyRotateResponse,
  ApiKeyListResponse,
} from '@/features/api-keys/types';
import { apiKeysApi } from '@/lib/api/apiKeys';
import { useOrganizationsStore } from './organizations';
import { useNotification } from '@asabdev/asab-ui';

export const useApiKeysStore = defineStore('apiKeys', () => {
  const organizationsStore = useOrganizationsStore();

  watch(
    () => organizationsStore.currentOrganizationId,
    newOrgId => {
      if (newOrgId) {
        refreshApiKeys();
      } else {
        resetStore();
      }
    }
  );

  // State
  const apiKeys = ref<ApiKey[]>([]);
  const selectedApiKey = ref<ApiKey | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const createKeyModalOpen = ref(false);
  const deleteKeyModalOpen = ref(false);
  const total = ref(0);
  const currentPage = ref(1);
  const perPage = ref(10);

  const { showSuccess, showError } = useNotification();

  // Computed
  const totalPages = computed(() => Math.ceil(total.value / perPage.value));
  const hasApiKeys = computed(() => apiKeys.value.length > 0);

  // Actions
  const clearError = () => {
    error.value = null;
  };

  const setLoading = (loading: boolean) => {
    isLoading.value = loading;
  };

  const fetchApiKeys = async (filters?: ApiKeyFilters, pagination?: Partial<ApiKeyPagination>) => {
    try {
      setLoading(true);
      clearError();

      const orgId = organizationsStore.currentOrganizationId;
      if (!orgId) {
        apiKeys.value = [];
        total.value = 0;
        return;
      }

      const page = pagination?.page || currentPage.value;
      const limit = pagination?.per_page || perPage.value;

      const response: ApiKey[] | ApiKeyListResponse = await apiKeysApi.listApiKeys(
        orgId,
        filters,
        { page, per_page: limit }
      );

      const apiKeysList = Array.isArray(response) ? response : [];
      apiKeys.value = apiKeysList;
      total.value = apiKeysList.length;
      currentPage.value = page;
    } catch (err) {
      const errorMessage =
        (err as { response?: { data?: { detail?: string } } }).response?.data?.detail ||
        'Failed to fetch API keys';
      error.value = errorMessage;
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const createApiKey = async (keyData: CreateApiKeyData): Promise<CreateApiKeyResponse | null> => {
    try {
      setLoading(true);
      clearError();

      const orgId = organizationsStore.currentOrganizationId;
      if (!orgId) throw new Error('No organization selected');

      const response: CreateApiKeyResponse = await apiKeysApi.createApiKey(orgId, keyData);

      // Add a summary record to the list (without the raw key)
      const newApiKey: ApiKey = {
        id: response.id,
        name: response.name,
        key_prefix: response.masked_key || '',
        key_type: response.key_type,
        scope: response.scope,
        permissions: response.permissions,
        created_by: '',
        created_at: response.created_at,
        expires_at: response.expires_at,
      };

      apiKeys.value.unshift(newApiKey);
      total.value += 1;
      closeCreateKeyModal();

      return response;
    } catch (err) {
      const errorMessage =
        (err as { response?: { data?: { detail?: string } } }).response?.data?.detail ||
        (err as Error).message ||
        'Failed to create API key';
      error.value = errorMessage;
      showError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const rotateApiKey = async (keyId: string): Promise<ApiKeyRotateResponse | null> => {
    try {
      setLoading(true);
      clearError();

      const orgId = organizationsStore.currentOrganizationId;
      if (!orgId) throw new Error('No organization selected');

      const response: ApiKeyRotateResponse = await apiKeysApi.rotateApiKey(orgId, keyId);
      showSuccess('API key rotated successfully!');
      return response;
    } catch (err) {
      const errorMessage =
        (err as { response?: { data?: { detail?: string } } }).response?.data?.detail ||
        (err as Error).message ||
        'Failed to rotate API key';
      error.value = errorMessage;
      showError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getApiKey = async (keyId: string): Promise<ApiKey | null> => {
    try {
      setLoading(true);
      clearError();

      const orgId = organizationsStore.currentOrganizationId;
      if (!orgId) throw new Error('No organization selected');

      const apiKey: ApiKey = await apiKeysApi.getApiKey(orgId, keyId);
      selectedApiKey.value = apiKey;
      return apiKey;
    } catch (err) {
      const errorMessage =
        (err as { response?: { data?: { detail?: string } } }).response?.data?.detail ||
        (err as Error).message ||
        'Failed to fetch API key';
      error.value = errorMessage;
      showError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateApiKey = async (keyId: string, updateData: UpdateApiKeyData): Promise<boolean> => {
    try {
      setLoading(true);
      clearError();

      const orgId = organizationsStore.currentOrganizationId;
      if (!orgId) throw new Error('No organization selected');

      const updatedKey: ApiKey = await apiKeysApi.updateApiKey(orgId, keyId, updateData);

      const keyIndex = apiKeys.value.findIndex(key => key.id === keyId);
      if (keyIndex !== -1) {
        apiKeys.value[keyIndex] = updatedKey;
      }

      if (selectedApiKey.value?.id === keyId) {
        selectedApiKey.value = updatedKey;
      }

      showSuccess('API key updated successfully!');
      return true;
    } catch (err) {
      const errorMessage =
        (err as { response?: { data?: { detail?: string } } }).response?.data?.detail ||
        (err as Error).message ||
        'Failed to update API key';
      error.value = errorMessage;
      showError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const deleteApiKey = async (keyId: string): Promise<boolean> => {
    try {
      setLoading(true);
      clearError();

      const orgId = organizationsStore.currentOrganizationId;
      if (!orgId) throw new Error('No organization selected');

      await apiKeysApi.deleteApiKey(orgId, keyId);

      apiKeys.value = apiKeys.value.filter(key => key.id !== keyId);
      total.value -= 1;

      if (selectedApiKey.value?.id === keyId) {
        selectedApiKey.value = null;
      }

      return true;
    } catch (err) {
      const errorMessage =
        (err as { response?: { data?: { detail?: string } } }).response?.data?.detail ||
        (err as Error).message ||
        'Failed to delete API key';
      error.value = errorMessage;
      showError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const getApiKeyUsage = async (
    keyId: string,
    days: number = 30
  ): Promise<ApiKeyUsageStats | null> => {
    try {
      setLoading(true);
      clearError();

      const orgId = organizationsStore.currentOrganizationId;
      if (!orgId) throw new Error('No organization selected');

      const usage: ApiKeyUsageStats = await apiKeysApi.getApiKeyUsageStats(orgId, keyId, days);
      return usage;
    } catch (err) {
      const errorMessage =
        (err as { response?: { data?: { detail?: string } } }).response?.data?.detail ||
        (err as Error).message ||
        'Failed to fetch API key usage';
      error.value = errorMessage;
      showError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const refreshApiKeys = async () => {
    await fetchApiKeys();
  };

  // Modal management
  const openCreateKeyModal = () => {
    createKeyModalOpen.value = true;
    clearError();
  };

  const closeCreateKeyModal = () => {
    createKeyModalOpen.value = false;
    clearError();
  };

  const openDeleteKeyModal = (apiKey: ApiKey) => {
    selectedApiKey.value = apiKey;
    deleteKeyModalOpen.value = true;
    clearError();
  };

  const closeDeleteKeyModal = () => {
    deleteKeyModalOpen.value = false;
    selectedApiKey.value = null;
    clearError();
  };

  // Pagination
  const setPage = (page: number) => {
    currentPage.value = page;
    fetchApiKeys();
  };

  const setPerPage = (size: number) => {
    perPage.value = size;
    currentPage.value = 1;
    fetchApiKeys();
  };

  // Utility functions
  const findApiKeyById = (keyId: string): ApiKey | undefined => {
    return apiKeys.value.find(key => key.id === keyId);
  };

  const copyApiKeyToClipboard = async (apiKey: string): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(apiKey);
      showSuccess('API key copied to clipboard!');
      return true;
    } catch {
      showError('Failed to copy API key to clipboard');
      return false;
    }
  };

  const resetStore = () => {
    apiKeys.value = [];
    selectedApiKey.value = null;
    isLoading.value = false;
    error.value = null;
    createKeyModalOpen.value = false;
    deleteKeyModalOpen.value = false;
    total.value = 0;
    currentPage.value = 1;
  };

  return {
    // State
    apiKeys,
    selectedApiKey,
    isLoading,
    error,
    createKeyModalOpen,
    deleteKeyModalOpen,
    total,
    currentPage,
    perPage,

    // Computed
    totalPages,
    hasApiKeys,

    // Actions
    clearError,
    setLoading,
    fetchApiKeys,
    createApiKey,
    rotateApiKey,
    getApiKey,
    updateApiKey,
    deleteApiKey,
    getApiKeyUsage,
    refreshApiKeys,

    // Modal management
    openCreateKeyModal,
    closeCreateKeyModal,
    openDeleteKeyModal,
    closeDeleteKeyModal,

    // Pagination
    setPage,
    setPerPage,

    // Utility functions
    findApiKeyById,
    copyApiKeyToClipboard,
    resetStore,
  };
});
